<template>
  <div class="carousel-wrapper" :style="styles">
    <div ref="carousel" class="carousel" @scroll="handleScroll">
      <slot />
    </div>
    <div class="controls">
      <Button size="small" icon="chevron-left" @click="handlePrevClick" />
      <Button size="small" icon="chevron-right" @click="handleNextClick" />
    </div>
  </div>
</template>

<script>
import Button from "@/components/ButtonV3.vue";

export default {
  name: "CarouselV3",
  components: {
    Button,
  },
  inject: ["mq"],
  props: {
    slideWidth: {
      type: String,
      default: "200px",
    },
    slideWidthTablet: {
      type: String,
      default: "260px",
    },
    slideWidthDesktop: {
      type: String,
      default: "300px",
    },
  },
  data() {
    return {
      observer: null,
      visibleSlides: [],
      items: [],
      intersectingEntries: [],
      scrollDirection: null,
      lastScrollLeft: 0,
      indexOfVisible: 0,
    };
  },
  computed: {
    styles() {
      return {
        "--carouselSlideWidth": this.slideWidth,
        "--carouselSlideWidthTablet": this.slideWidthTablet,
        "--carouselSlideWidthDesktop": this.slideWidthDesktop,
      };
    },
  },
  mounted() {
    this.initializeCarousel();
  },
  beforeUnmount() {
    this.disconnectObserver();
  },
  methods: {
    initializeCarousel() {
      this.visibleSlides = [...this.$refs.carousel.children];
      this.items = [...this.$refs.carousel.children];

      this.$nextTick(() => {
        this.initializeObserver();
      });
    },

    initializeObserver() {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        root: this.$refs.carousel,
        threshold: 0.5,
      });

      this.items.forEach((item) => this.observer.observe(item));
    },

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },

    handleIntersection(entries) {
      entries.forEach((entry) => {
        entry.isIntersecting
          ? this.addIntersectingEntry(entry)
          : this.removeIntersectingEntry(entry);
      });

      this.handleCarouselLoop();
    },

    addIntersectingEntry(entry) {
      if (!this.intersectingEntries.includes(entry)) {
        this.intersectingEntries.push(entry);
      }
      entry.target.dataset.intersected = true;
    },

    removeIntersectingEntry(entry) {
      this.intersectingEntries = this.intersectingEntries.filter(
        (el) => !el.target.isSameNode(entry.target)
      );
      entry.target.dataset.intersected = false;
    },

    getCenterMostEntry() {
      const viewportCenterX = window.innerWidth / 2;

      return this.intersectingEntries.reduce((centerMost, current) => {
        const centerX = (el) =>
          el.target.getBoundingClientRect().x +
          el.target.getBoundingClientRect().width / 2;

        const centerMostDistance = Math.abs(
          centerX(centerMost) - viewportCenterX
        );
        const currentDistance = Math.abs(centerX(current) - viewportCenterX);

        return currentDistance < centerMostDistance ? current : centerMost;
      }, this.intersectingEntries[0]);
    },

    handleScroll() {
      const currentScrollLeft = this.$refs.carousel.scrollLeft;
      this.scrollDirection =
        currentScrollLeft > this.lastScrollLeft ? "right" : "left";
      this.lastScrollLeft = currentScrollLeft;

      this.handleCarouselLoop();
    },

    handleCarouselLoop() {
      const { scrollLeft, scrollWidth, clientWidth } = this.$refs.carousel;

      if (
        this.scrollDirection === "right" &&
        scrollLeft + clientWidth >= scrollWidth &&
        this.mq.mdPlus
      ) {
        this.moveFirstSlideToEnd();
        this.$refs.carousel.scrollLeft -= this.items[0].offsetWidth;
      } else if (
        this.scrollDirection === "left" &&
        scrollLeft <= 0 &&
        this.mq.mdPlus
      ) {
        this.moveLastSlideToStart();
        this.$refs.carousel.scrollLeft += this.items[0].offsetWidth;
      }
    },

    moveFirstSlideToEnd() {
      const firstSlide = this.items.shift();
      this.$refs.carousel.appendChild(firstSlide);
      this.items.push(firstSlide);
    },

    moveLastSlideToStart() {
      const lastSlide = this.items.pop();
      this.$refs.carousel.insertBefore(lastSlide, this.items[0]);
      this.items.unshift(lastSlide);
    },

    updateVisibleIndex(entry) {
      if (entry) {
        const targetIndex = this.items.findIndex((el) =>
          el.isSameNode(entry.target)
        );
        this.indexOfVisible = targetIndex !== -1 ? targetIndex : 0;
      }
    },

    handlePrevClick() {
      // Scroll the item to the left of the center-most entry into view
      const centerMostEntry = this.getCenterMostEntry();
      const centerIndex = this.items.findIndex((el) =>
        el.isSameNode(centerMostEntry.target)
      );
      if (centerIndex > 0) {
        this.items[centerIndex - 1].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    },

    handleNextClick() {
      // Scroll the item to the right of the center-most entry into view
      const centerMostEntry = this.getCenterMostEntry();
      const centerIndex = this.items.findIndex((el) =>
        el.isSameNode(centerMostEntry.target)
      );
      if (centerIndex < this.items.length - 1) {
        this.items[centerIndex + 1].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.carousel {
  display: flex;
  overflow-y: hidden;
  overflow-x: scroll;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;

  & > :slotted(*) {
    flex: 0 0 var(--carouselSlideWidth);

    @include respond-above(md) {
      flex: 0 0 var(--carouselSlideWidthTablet);
    }

    @include respond-above(lg) {
      flex: 0 0 var(--carouselSlideWidthDesktop);
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.header {
  margin: 0 auto;
  width: 350px;
  text-align: center;
  margin-bottom: 40px;

  h4 {
    @include editorial-headline(24px);
    text-transform: uppercase;
    margin-bottom: 16px;
  }

  p {
    @include editorial-body(14px);
    text-wrap: balance;
  }
}

.controls {
  display: none;
  margin-top: 60px;

  @include respond-above(md) {
    display: flex;
    justify-content: center;
    gap: 12px;
  }
}
</style>
